import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import SEO from '../../components/SEO';
import { Button } from '../../components/ui/button';
import { ArrowRight, MapPin, Phone, Clock } from 'lucide-react';

/**
 * SEO landing page — "Location caméra cinéma à Paris".
 * Long-form editorial content targeting the search intent
 * "location caméra cinéma paris" and related long-tails.
 */
const LocationCameraCinemaParis = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const canonical = 'https://www.puzzlevideo.fr/guides/location-camera-cinema-paris';
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: 'Location de caméra cinéma à Paris — guide complet',
    description:
      "Comment louer une caméra cinéma à Paris : modèles disponibles, tarifs, accessoires, livraison en Île-de-France. Le guide Puzzle Video.",
    author: { '@type': 'Organization', name: 'Puzzle Video' },
    publisher: {
      '@type': 'Organization',
      name: 'Puzzle Video',
      logo: {
        '@type': 'ImageObject',
        url: 'https://for-rental-staging.s3.eu-west-3.amazonaws.com/eqgootqxdu25amnn1whuqc1u53tk',
      },
    },
    mainEntityOfPage: canonical,
    datePublished: '2026-02-11',
    dateModified: '2026-02-11',
    inLanguage: 'fr-FR',
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Location caméra cinéma à Paris — Sony, ARRI, RED, Blackmagic"
        description="Louez une caméra cinéma à Paris chez Puzzle Video : Sony FX6/FX9, ARRI Alexa Mini, RED, Blackmagic. Tarifs HT/jour, retrait 75012 ou livraison Île-de-France. Devis gratuit."
        url={canonical}
        jsonLd={jsonLd}
      />
      <Helmet>
        <link rel="canonical" href={canonical} />
      </Helmet>

      <article className="max-w-3xl mx-auto px-4 py-12">
        {/* Breadcrumb */}
        <nav className="text-xs text-muted-foreground mb-6" aria-label="Fil d'Ariane">
          <Link to="/" className="hover:text-primary">Accueil</Link>
          <span className="mx-2">/</span>
          <Link to="/guides" className="hover:text-primary">Guides</Link>
          <span className="mx-2">/</span>
          <span>Location caméra cinéma à Paris</span>
        </nav>

        <header className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-primary mb-3">Guide de location</p>
          <h1 className="text-4xl sm:text-5xl font-extrabold tracking-tight mb-4">
            Location de caméra cinéma à Paris
          </h1>
          <p className="text-lg text-muted-foreground">
            Tout ce qu&apos;il faut savoir avant de louer votre caméra pour un tournage à Paris ou en
            Île-de-France&nbsp;: modèles disponibles, tarifs HT/jour, accessoires inclus, options de
            livraison et retrait.
          </p>
        </header>

        <section className="prose prose-lg max-w-none mb-10 space-y-4">
          <h2 className="text-2xl font-bold mt-8 mb-3">Où louer une caméra cinéma à Paris&nbsp;?</h2>
          <p>
            Puzzle Video est un loueur de matériel audiovisuel professionnel basé au{' '}
            <strong>65 rue Baron Leroy, 75012 Paris</strong>. Retrait et retour du matériel du{' '}
            <strong>lundi au vendredi de 9h30 à 12h30 et de 13h30 à 18h30</strong>. Accueil planning
            et administratif de 10h à 12h30 et de 14h à 18h30. Les caméras cinéma constituent notre cœur
            d&apos;activité&nbsp;: nous stockons les principaux modèles utilisés en production
            professionnelle (documentaire, publicité, série, long-métrage).
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-3">Quelles caméras louer selon votre projet&nbsp;?</h2>
          <p>
            Chaque tournage a ses contraintes techniques et budgétaires. Voici notre recommandation par
            type de projet&nbsp;:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>Documentaire, interview, clip corporate</strong> —{' '}
              <Link to="/catalogue?nav_menu=IMAGE" className="text-primary underline">Sony FX3, FX6</Link>{' '}
              : compactes, autofocus fiable, workflow rapide.
            </li>
            <li>
              <strong>Publicité, court-métrage, cinéma indépendant</strong> —{' '}
              <Link to="/catalogue?nav_menu=IMAGE" className="text-primary underline">Sony FX9, RED Komodo, Blackmagic URSA</Link>{' '}
              : rendu Super 35, colorimétrie de post-production, RAW.
            </li>
            <li>
              <strong>Long-métrage, série broadcast, cinéma haut de gamme</strong> —{' '}
              <Link to="/catalogue?nav_menu=IMAGE" className="text-primary underline">ARRI Alexa Mini, Alexa Mini LF</Link>{' '}
              : référence absolue en dynamique et flexibilité colorimétrique.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-3">Combien coûte la location d&apos;une caméra à Paris&nbsp;?</h2>
          <p>
            Nos tarifs sont exprimés <strong>HT par jour ouvré</strong>. Un coefficient dégressif
            s&apos;applique au-delà de plusieurs jours consécutifs, ce qui rend les tournages longs
            plus économiques. Les fourchettes indicatives (2026)&nbsp;:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>Sony FX3 / FX6&nbsp;: 120–170&nbsp;€ HT/jour</li>
            <li>Sony FX9 / RED Komodo&nbsp;: 250–350&nbsp;€ HT/jour</li>
            <li>ARRI Alexa Mini&nbsp;: à partir de 550&nbsp;€ HT/jour</li>
          </ul>
          <p>
            Consultez chaque fiche produit du{' '}
            <Link to="/catalogue" className="text-primary underline">catalogue</Link>{' '}
            pour le tarif exact et les accessoires inclus (poignée, chargeur, batteries de base, etc.).
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-3">Livraison ou retrait sur Paris et Île-de-France</h2>
          <p>
            Deux options s&apos;offrent à vous&nbsp;:
          </p>
          <ul className="list-disc list-inside space-y-2">
            <li>
              <strong>Retrait à Paris 12e</strong> — 65 rue Baron Leroy, 75012, gratuit.
            </li>
            <li>
              <strong>Livraison + récupération en Île-de-France</strong> — tarifs à la zone
              (Paris intra-muros, petite couronne, grande couronne). Créneau matin ou après-midi,
              option urgente disponible.
            </li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-3">Assurance et caution</h2>
          <p>
            Le matériel loué est couvert par une assurance de <strong>8&nbsp;%</strong> du sous-total
            location, incluse par défaut au panier. Une caution est demandée au départ&nbsp;: son
            montant dépend de la valeur du parc réservé. Notre équipe vous précisera le montant exact
            au moment de la validation du devis.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-3">Comment réserver&nbsp;?</h2>
          <p>
            La démarche est simple&nbsp;:
          </p>
          <ol className="list-decimal list-inside space-y-2">
            <li>
              Parcourez notre{' '}
              <Link to="/catalogue" className="text-primary underline">catalogue</Link>{' '}
              et ajoutez les caméras + accessoires souhaités au panier.
            </li>
            <li>Choisissez vos dates de retrait et de retour.</li>
            <li>Optionnel&nbsp;: activez la livraison et/ou la récupération avec adresse et créneau.</li>
            <li>Envoyez votre demande de devis — vous recevez un mail immédiat avec le récap.</li>
            <li>Notre équipe vous rappelle pour confirmer la disponibilité et finaliser le dossier.</li>
          </ol>
        </section>

        {/* CTA */}
        <div className="mt-12 p-6 bg-gray-50 border-l-4 border-primary space-y-3">
          <h3 className="text-xl font-bold">Prêt à réserver votre caméra&nbsp;?</h3>
          <p className="text-sm text-muted-foreground">
            Parcourez le catalogue ou contactez-nous&nbsp;:
          </p>
          <div className="flex flex-wrap gap-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" /> 65 rue Baron Leroy, 75012 Paris</span>
            <span className="inline-flex items-center gap-1"><Phone className="w-3 h-3" /> 01 45 85 81 76</span>
            <span className="inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Lun–Ven 9h30–12h30 / 13h30–18h30</span>
          </div>
          <Link to="/catalogue">
            <Button className="rounded-none">
              Voir le catalogue <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </article>
    </div>
  );
};

export default LocationCameraCinemaParis;
